/* global Event */
import React, { useCallback, useMemo, useRef, useEffect, useState, useImperativeHandle } from 'react'
import { message } from 'antd'
import _isFunction from 'lodash/isFunction'
import { useQuery, useMutation } from '@apollo/react-hooks'
import I18n from 'i18n-js'

import { UPDATE_COMPANY_SETTING, UPDATE_DEFAULT_TENANT_SETTING } from '../../components/Queries/Companies'
import { GET_COURSES, CREATE_COURSE_PREVIEW_RESULT } from '../../components/Queries/Courses'
import { LoadingBlock } from '../../components/common'
import { useHasSessionPermission } from '../../hooks'
import useGlobalState from '../../hooks/useGlobalState'
import { SettingsFormElement } from '../../components/Settings/SettingsForm'
import { getSettingsFromCompany } from '../../helpers/getMe'
import { renderParagraphsFragmentFromArray } from '../../helpers'
import { permissions } from '../../constants/permissions'
import { getRequiredUpdatePermissions } from '../../helpers/settings'
import { getBrowserLocale } from '../../helpers/locale'
import authenticatedClient from '../../apollo-client/authenticatedClient'

const trOpt = { scope: 'uPhish.inlineTraining' }

const InlineTrainingForm = ({ inlineTraining, uLearnEnabled, updateSettings, refetchQueries, defaultTenant, forwardedRef, disableSubmit }) => {
  const { hasAllSessionPermissions } = useHasSessionPermission()
  const { userLocale } = useGlobalState(
    state => ({
      userLocale: state?.session?.locale
    })
  )
  const [loading, setLoading] = useState(true)
  const [courseOptions, setCourseOptions] = useState([])
  const [, setForceRender] = useState(null) // Forces re-render to bump has changed state
  const form = useRef(null)
  const initialiseForm = useCallback(async () => {
    if (form && form.current) {
      // Clone the inline training settings to avoid modifying the original
      const initialValues = { ...inlineTraining }
      // Convert courseIds from array of values to array of objects with id, value and label properties
      if (initialValues.courseIds && Array.isArray(initialValues.courseIds)) {
        initialValues.courseIds = initialValues.courseIds.map(courseId => {
          // Find the course in courseOptions to get its label
          const courseOption = courseOptions.find(option => option.value === courseId)
          return {
            id: `course-${courseId}`, // Generate a stable ID based on the course ID
            value: courseId,
            label: courseOption ? courseOption.label : courseId.toString() // Fallback to ID if label not found
          }
        })
      }
      await form.current.setInitialValues(initialValues)
      setForceRender(Date.now())
      setLoading(false)
    }
  }, [inlineTraining, form, courseOptions])
  useEffect(() => {
    initialiseForm()
  }, [initialiseForm, defaultTenant])
  const onChange = useCallback(id => {
    if (id === 'enabled') {
      window.dispatchEvent(new Event('resize'))
    }
    // Ensure courseIds is always treated as an array
    if (id === 'courseIds' && form.current) {
      const value = form.current.getFieldValue('courseIds')
      if (value && !Array.isArray(value)) {
        form.current.setField('courseIds', [])
      }
    }
    setForceRender(Date.now())
  }, [form])

  const requiredUpdatePermissions = getRequiredUpdatePermissions({
    defaultTenant,
    requiredPermissions: [permissions.SETTINGS_UPHISH_UPDATE]
  })
  const isUpdateAllowed = hasAllSessionPermissions(requiredUpdatePermissions)
  const hasChanged = useCallback(() => isUpdateAllowed && form.current?.hasChanged() === true, [form, isUpdateAllowed])

  useImperativeHandle(forwardedRef, () => ({
    hasChanged
  }), [hasChanged])

  const { data: coursesData } = useQuery(GET_COURSES, {
    variables: { excludeGapAnalysis: true, restrictToPlan: true, withCompany: true },
    skip: !uLearnEnabled
  })

  useEffect(() => {
    const { courses = [] } = coursesData ?? {}
    setCourseOptions(courses
    // Sort by difficulty then sort by displayName
      .sort((a, b) => a.difficulty - b.difficulty || a.displayName.toLowerCase() > b.displayName.toLowerCase())
      .map(course => ({ value: course.id, label: course.displayName, linkFieldValue: course.subject }))
    )
  }, [coursesData])

  const handleCoursePreview = useCallback(async (item) => {
    if (item?.value) {
      authenticatedClient.mutate({
        mutation: CREATE_COURSE_PREVIEW_RESULT,
        variables: { courseId: item.value, locale: userLocale || getBrowserLocale() }
      }).then(({ data: { createCoursePreviewResult } }) => {
        window.open(`/preview-course/${createCoursePreviewResult}`, '_blank')
      }).catch((error) => {
        message.error(error.message)
      })
    }
  }, [userLocale])

  const TRIGGER_EVENTS_OPTIONS = ['visit', 'compromise'].map((simulationEvent) => { return { value: simulationEvent, get label () { return I18n.t(`triggerEvents.${simulationEvent}`, trOpt) } } })
  const fields = useMemo(() => {
    // Default Customer Settings version of Inline Training allows MSPs to configure the feature with it needing to be activated immediately.
    // An MSP can then turn on inline training on a new customer when ready and the configuration will be prepopulated.
    return [
      {
        id: 'enabled',
        label: I18n.t('enableInlineTraining', trOpt),
        type: 'switch',
        defaultValue: false
      },
      {
        id: 'triggerEvent',
        label: I18n.t('triggerEvent', trOpt),
        type: 'radio',
        options: TRIGGER_EVENTS_OPTIONS,
        defaultValue: 'compromise'
      },
      {
        id: 'courseIds',
        label: I18n.t('courses', trOpt),
        type: 'dragAndDropList',
        options: courseOptions,
        showPreview: true,
        showEdit: false,
        onPreviewClick: handleCoursePreview,
        defaultValue: []
      }
    ]
  }, [
    courseOptions, TRIGGER_EVENTS_OPTIONS, handleCoursePreview
  ])

  const mutateValues = useCallback(values => {
    // Ensure courseIds is always an array
    if (values.courseIds && !Array.isArray(values.courseIds)) {
      values.courseIds = []
    }
    // Create a clone of values to avoid modifying the original
    const mutationValues = { ...values }
    // Transform courseIds from array of objects to array of values for the mutation
    if (mutationValues.courseIds && Array.isArray(mutationValues.courseIds)) {
      // Store the original array for reference
      const originalCourseIds = [...mutationValues.courseIds]
      // Transform to array of values for the mutation
      mutationValues.courseIds = originalCourseIds.map(course => course.value)
    }
    return mutationValues
  }, [])

  const [saveSettings] = useMutation(defaultTenant ? UPDATE_DEFAULT_TENANT_SETTING : UPDATE_COMPANY_SETTING, { refetchQueries })
  const onSuccess = useCallback(result => {
    message.success(I18n.t('successfullyUpdatedInlineTrainingSettings', trOpt))
    if (_isFunction(updateSettings)) {
      const company = result?.data?.[defaultTenant ? 'updateCompanySetting' : 'updateDefaultTenantSetting']
      if (company) {
        const settings = getSettingsFromCompany(company)
        if (settings) {
          updateSettings(settings)
        }
      }
    }
  }, [updateSettings, defaultTenant])
  const onSubmit = useCallback(async (values, errors, variables) => {
    try {
      setLoading(true)
      const results = await saveSettings({ variables })
      onSuccess(results)
    } finally {
      setLoading(false)
    }
  }, [saveSettings, onSuccess])

  return (
    <>
      <LoadingBlock loading={loading} fullScreen={false} />
      <SettingsFormElement
        headerId='settings-inline-training-header'
        title={I18n.t('title', trOpt)}
        desc={renderParagraphsFragmentFromArray(I18n.t('description', trOpt))}
        formProps={{
          ref: form,
          fields,
          variables: { settingId: 'simulationInlineTraining' },
          valuesObjectName: 'setting',
          mutateValues,
          onChange,
          onSubmit,
          refetchQueries,
          failureMessage: I18n.t('anErrorOccurredAttemptingToUpdate', trOpt),
          skipResetFieldsOnSubmit: true,
          disableSubmitIfInvalid: false
        }}
        disableSubmit={disableSubmit || !isUpdateAllowed}
        isUpdateAllowed={isUpdateAllowed}
        hasChanged={hasChanged()}
      />
    </>
  )
}

const InlineTraining = React.forwardRef(({ defaultTenant, settings: companySettings, updateSettings, disableSubmit }, ref) => {
  const inlineTraining = companySettings?.simulationInlineTraining ?? {}
  const uLearnEnabled = companySettings?.uLearn === true || defaultTenant
  return (
    <InlineTrainingForm
      {...{ updateSettings, defaultTenant, disableSubmit, inlineTraining, uLearnEnabled, forwardedRef: ref }}
    />
  )
})

export default InlineTraining
